import type { WatchSource } from 'vue';

interface UseStoreOptions<T> {
	key: string;
	/**
	 * Returns already-validated data. Validation is the fetch boundary's job —
	 * call `$api(url, { schema })` inside the fetcher so the value that reaches
	 * here is guaranteed to match its type. `useStore` never validates.
	 */
	fetcher: () => Promise<T>;
	/** Reactive sources that trigger a refetch when they change. */
	watch?: WatchSource[];
	/** Fetch during setup. Skipped automatically during static prerender. Default: true */
	immediate?: boolean;
	/** Defer the fetch until first access. Default: false */
	lazy?: boolean;
	/** Run on the server (SSR). Default: true */
	server?: boolean;
	/**
	 * Reuse an already-resolved result for this key across initial mounts instead
	 * of refetching. Set false to always hit the network on mount (fresh every
	 * time). Default: true
	 */
	cache?: boolean;
}

/**
 * Thin wrapper over `useAsyncData` that gives every store the same shape and a
 * single shared, deduped fetch per `key`. Mounting the same store from multiple
 * components reuses one cached result rather than firing N requests; `refresh()`
 * is the only thing that refetches.
 */
export function useStore<T>(options: UseStoreOptions<T>) {
	const {
		key,
		fetcher,
		watch = [],
		immediate = true,
		lazy = false,
		server = true,
		cache = true,
	} = options;

	const { data, refresh, status, error } = useAsyncData<T>(key, fetcher, {
		watch,
		// Never fetch during static prerender — these are runtime data sources.
		immediate: immediate && !import.meta.prerender,
		lazy,
		server,
		// Dedupe only across initial mounts: reuse an already-resolved result for
		// this key instead of refetching on every mount. Manual refresh,
		// refreshNuxtData (cause `refresh:hook`), and watch-triggered refetches
		// must bypass the cache and hit the network — returning a value here for
		// those causes would suppress the request entirely.
		// When `cache` is false, never reuse a cached result: always fetch fresh.
		getCachedData: cache
			? (cacheKey, nuxtApp, ctx) => {
					if (ctx.cause !== 'initial') return undefined;
					return (nuxtApp.payload.data[cacheKey] ?? nuxtApp.static.data[cacheKey]) as
						| T
						| undefined;
				}
			: () => undefined,
	});

	const loading = computed(() => status.value === 'pending');
	// Re-fetching while stale data is still on screen (background refresh).
	const refreshing = computed(() => status.value === 'pending' && data.value != null);

	return {
		data,
		status,
		loading,
		refreshing,
		error,
		refresh,
	};
}
